// src/pages/OCICosts.js (modified to include the Data vs Price Analysis Widget)
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  useTheme, 
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField
} from '@mui/material';
import { 
  AttachMoney as CostIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Storage as StorageIcon,
  Database as DatabaseIcon,
  Language as NetworkIcon,
  Dns as ComputeIcon,
  Tune as MiscIcon,
  Cloud as CloudIcon,
  CloudDownload as DownloadIcon,
  DateRange as DateRangeIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';

// Chart components
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';

// Custom components
import PageHeader from '../components/common/PageHeader';
import ChartCard from '../components/common/ChartCard';
import LoadingOverlay from '../components/common/LoadingOverlay';
import EmptyState from '../components/common/EmptyState';
import DataPriceAnalysisWidget from '../components/common/DataPriceAnalysisWidget'; // Import the new widget
import api from '../services/api';
import ociMonitoringApi from '../services/ociMonitoringApi';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Helper function to format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '₹0.00';
  return `₹${value.toLocaleString(undefined, { 
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

// Utility to get chart colors
const getChartColors = (index = 0, alpha = 1) => {
  const colors = [
    `rgba(58, 134, 255, ${alpha})`,   // blue (primary)
    `rgba(76, 201, 189, ${alpha})`,   // teal (secondary)
    `rgba(255, 183, 77, ${alpha})`,   // amber (tertiary)
    `rgba(255, 153, 102, ${alpha})`,  // orange (quaternary)
    `rgba(76, 175, 80, ${alpha})`,    // green
    `rgba(33, 150, 243, ${alpha})`,   // light blue
    `rgba(156, 39, 176, ${alpha})`,   // purple
    `rgba(233, 30, 99, ${alpha})`,    // pink
    `rgba(0, 150, 136, ${alpha})`,    // teal
    `rgba(63, 81, 181, ${alpha})`,    // indigo
    `rgba(121, 85, 72, ${alpha})`,    // brown
    `rgba(96, 125, 139, ${alpha})`,   // blue grey
  ];
  
  return colors[index % colors.length];
};

// Helper function to format date in YYYY-MM-DD format
const formatDateString = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Helper function to subtract days from a date
const subtractDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
};

// Helper function to add days to a date
const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const OCICosts = () => {
  const theme = useTheme();
  
  // State for filters
  const [region, setRegion] = useState('');
  const [compartment, setCompartment] = useState('');
  const [startDateStr, setStartDateStr] = useState(formatDateString(subtractDays(new Date(), 7)));
  const [endDateStr, setEndDateStr] = useState(formatDateString(new Date()));
  
  // Data state
  const [regions, setRegions] = useState([]);
  const [compartments, setCompartments] = useState([]);
  const [costData, setCostData] = useState([]);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Loading state
  const [loading, setLoading] = useState({
    regions: false,
    compartments: false,
    costs: false
  });
  
  // Error state
  const [error, setError] = useState(null);
  
  // Fetch regions data from actual API
  const fetchRegions = async () => {
    setLoading(prev => ({ ...prev, regions: true }));
    try {
      // Call the regions endpoint directly using the correct URL path
      const response = await api.get('/api/oci/regions');
      console.log('Regions response:', response.data);
      setRegions(response.data || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
      setError('Failed to fetch regions. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, regions: false }));
    }
  };
  
  // Fetch compartments from actual API
  const fetchCompartments = async (selectedRegion) => {
    setLoading(prev => ({ ...prev, compartments: true }));
    try {
      // Call the compartments endpoint directly using the correct URL path
      const url = `/api/oci/compartments${selectedRegion ? `?region=${selectedRegion}` : ''}`;
      console.log(`Fetching compartments from: ${url}`);
      
      const response = await api.get(url);
      console.log('Compartments response:', response.data);
      
      setCompartments(response.data || []);
    } catch (error) {
      console.error('Error fetching compartments:', error);
      setError('Failed to fetch compartments. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, compartments: false }));
    }
  };
  
  // Fetch costs from actual API
  const fetchCosts = async () => {
    setLoading(prev => ({ ...prev, costs: true }));
    setError(null);
    
    try {
      // Call using a direct API request to the correct endpoint
      const url = `/api/oci/oci_costs?start_date=${startDateStr}&end_date=${endDateStr}${region ? `&region=${region}` : ''}${compartment ? `&compartment=${compartment}` : ''}`;
      
      console.log(`Fetching cost data from: ${url}`);
      const response = await api.get(url);
      
      // Handle the API response format
      let costDataFormatted = [];
      
      if (response?.data && Array.isArray(response.data)) {
        // If the API returns an array of daily cost data
        costDataFormatted = response.data;
        console.log(`Received ${costDataFormatted.length} cost data records`);
      } else if (response?.data) {
        // Handle different API response formats if needed
        console.log("Cost data is not an array, converting format");
        // Convert the response data to the expected format
        costDataFormatted = Object.entries(response.data).map(([date, costs]) => ({
          date,
          ...costs
        }));
      }
      
      // Sort by date
      costDataFormatted.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setCostData(costDataFormatted);
    } catch (error) {
      console.error('Error fetching costs:', error);
      setError('Failed to fetch cost data. Please try again. ' + (error.message || ''));
      setCostData([]);
    } finally {
      setLoading(prev => ({ ...prev, costs: false }));
    }
  };
  
  // Load all data
  const loadAllData = () => {
    fetchRegions();
    fetchCompartments(region);
    fetchCosts();
  };
  
  // Handle refresh
  const handleRefresh = () => {
    loadAllData();
  };
  
  // Initialize data load
  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Update compartments when region changes
  useEffect(() => {
    fetchCompartments(region);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);
  
  /// Update costs when filters change
  useEffect(() => {
    fetchCosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, compartment, startDateStr, endDateStr]);
  
  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Process data for charts and tables
  
  // Daily cost trend
  const dailyCostTrendData = useMemo(() => {
    if (!costData || costData.length === 0) return null;
    
    return {
      labels: costData.map(item => item.date),
      datasets: [{
        label: 'Total Cost (₹)',
        data: costData.map(item => item.grand_total_cost || 0),
        borderColor: getChartColors(0),
        backgroundColor: getChartColors(0, 0.1),
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5
      }]
    };
  }, [costData]);
  
  // Service breakdown doughnut chart
  const serviceBreakdownData = useMemo(() => {
    if (!costData || costData.length === 0) return null;
    
    // Get the latest date's data
    const latestCost = costData[costData.length - 1];
    
    // Create datasets for different service categories
    const computeCost = latestCost.total_compute_cost || 0;
    const storageCost = (
      (latestCost.total_block_storage_cost || 0) + 
      (latestCost.total_file_storage_cost || 0) + 
      (latestCost.total_object_storage_cost || 0)
    );
    const networkCost = (
      (latestCost.total_load_balancer_cost || 0) + 
      (latestCost.total_virtual_cloud_network_cost || 0)
    );
    const databaseCost = latestCost.total_mysql_cost || 0;
    const otherCost = (
      (latestCost.total_functions_cost || 0) + 
      (latestCost.total_telemetry_cost || 0) + 
      (latestCost.total_api_gateway_cost || 0) + 
      (latestCost.total_container_engine_cost || 0) + 
      (latestCost.total_key_management_cost || 0) + 
      (latestCost.total_logging_cost || 0)
    );
    
    return {
      labels: ['Compute', 'Storage', 'Network', 'Database', 'Other'],
      datasets: [{
        data: [computeCost, storageCost, networkCost, databaseCost, otherCost],
        backgroundColor: [
          getChartColors(0, 0.7),
          getChartColors(1, 0.7),
          getChartColors(2, 0.7),
          getChartColors(3, 0.7),
          getChartColors(4, 0.7)
        ],
        borderColor: [
          getChartColors(0),
          getChartColors(1),
          getChartColors(2),
          getChartColors(3),
          getChartColors(4)
        ],
        borderWidth: 1
      }]
    };
  }, [costData]);
  
  // Services stacked bar chart for trend analysis
  const servicesTrendData = useMemo(() => {
    if (!costData || costData.length === 0) return null;
    
    return {
      labels: costData.map(item => item.date),
      datasets: [
        { label: 'Compute (₹)', data: costData.map(item => item.total_compute_cost || 0), backgroundColor: getChartColors(0, 0.7), borderColor: getChartColors(0), borderWidth: 1, stack: 'stack1' },
        { label: 'Storage (₹)', data: costData.map(item => ((item.total_block_storage_cost || 0) + (item.total_file_storage_cost || 0) + (item.total_object_storage_cost || 0))), backgroundColor: getChartColors(1, 0.7), borderColor: getChartColors(1), borderWidth: 1, stack: 'stack1' },
        { label: 'Network (₹)', data: costData.map(item => ((item.total_load_balancer_cost || 0) + (item.total_virtual_cloud_network_cost || 0))), backgroundColor: getChartColors(2, 0.7), borderColor: getChartColors(2), borderWidth: 1, stack: 'stack1' },
        { label: 'Database (₹)', data: costData.map(item => item.total_mysql_cost || 0), backgroundColor: getChartColors(3, 0.7), borderColor: getChartColors(3), borderWidth: 1, stack: 'stack1' },
        { label: 'Other (₹)', data: costData.map(item => ((item.total_functions_cost || 0) + (item.total_telemetry_cost || 0) + (item.total_api_gateway_cost || 0) + (item.total_container_engine_cost || 0) + (item.total_key_management_cost || 0) + (item.total_logging_cost || 0))), backgroundColor: getChartColors(4, 0.7), borderColor: getChartColors(4), borderWidth: 1, stack: 'stack1' }
      ]
    };
  }, [costData]);
  
  // Storage costs breakdown
  const storageBreakdownData = useMemo(() => {
    if (!costData || costData.length === 0) return null;
    
    // Get the latest date's data
    const latestCost = costData[costData.length - 1];
    
    return {
      labels: ['Block Storage', 'File Storage', 'Object Storage'],
      datasets: [{
        data: [
          latestCost.total_block_storage_cost || 0,
          latestCost.total_file_storage_cost || 0,
          latestCost.total_object_storage_cost || 0
        ],
        backgroundColor: [
          getChartColors(5, 0.7),
          getChartColors(6, 0.7),
          getChartColors(7, 0.7)
        ],
        borderColor: [
          getChartColors(5),
          getChartColors(6),
          getChartColors(7)
        ],
        borderWidth: 1
      }]
    };
  }, [costData]);
  
  // Other costs breakdown
  const otherCostsBreakdownData = useMemo(() => {
    if (!costData || costData.length === 0) return null;
    
    // Get the latest date's data
    const latestCost = costData[costData.length - 1];
    
    return {
      labels: [
        'Functions', 
        'Telemetry', 
        'API Gateway', 
        'Container Engine', 
        'Key Management', 
        'Logging'
      ],
      datasets: [{
        data: [
          latestCost.total_functions_cost || 0,
          latestCost.total_telemetry_cost || 0,
          latestCost.total_api_gateway_cost || 0,
          latestCost.total_container_engine_cost || 0,
          latestCost.total_key_management_cost || 0,
          latestCost.total_logging_cost || 0
        ],
        backgroundColor: [
          getChartColors(8, 0.7),
          getChartColors(9, 0.7),
          getChartColors(10, 0.7),
          getChartColors(11, 0.7),
          getChartColors(0, 0.7),
          getChartColors(1, 0.7)
        ],
        borderColor: [
          getChartColors(8),
          getChartColors(9),
          getChartColors(10),
          getChartColors(11),
          getChartColors(0),
          getChartColors(1)
        ],
        borderWidth: 1
      }]
    };
  }, [costData]);
  
  // Calculate total and monthly average costs
  const costSummary = useMemo(() => {
    if (!costData || costData.length === 0) {
      return {
        totalCost: 0,
        averageDailyCost: 0,
        projectedMonthlyCost: 0,
        costByService: {}
      };
    }
    
    const totalCost = costData.reduce((sum, item) => sum + (item.grand_total_cost || 0), 0);
    const averageDailyCost = totalCost / costData.length;
    const projectedMonthlyCost = averageDailyCost * 30;
    
    // Calculate costs by service
    const latestCost = costData[costData.length - 1];
    const costByService = {
      compute: latestCost.total_compute_cost || 0,
      storage: (
        (latestCost.total_block_storage_cost || 0) + 
        (latestCost.total_file_storage_cost || 0) + 
        (latestCost.total_object_storage_cost || 0)
      ),
      network: (
        (latestCost.total_load_balancer_cost || 0) + 
        (latestCost.total_virtual_cloud_network_cost || 0)
      ),
      database: latestCost.total_mysql_cost || 0,
      other: (
        (latestCost.total_functions_cost || 0) + 
        (latestCost.total_telemetry_cost || 0) + 
        (latestCost.total_api_gateway_cost || 0) + 
        (latestCost.total_container_engine_cost || 0) + 
        (latestCost.total_key_management_cost || 0) + 
        (latestCost.total_logging_cost || 0)
      )
    };
    
    return {
      totalCost,
      averageDailyCost,
      projectedMonthlyCost,
      costByService
    };
  }, [costData]);
  
  // Paginated cost data for the table
  const paginatedCostData = useMemo(() => {
    return costData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [costData, page, rowsPerPage]);
  
  // Chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          color: theme.palette.text.secondary,
        },
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: alpha(theme.palette.divider, 0.2),
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatCurrency(context.parsed.y);
            }
            return label;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: alpha(theme.palette.text.secondary, 0.1),
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          callback: function(value) {
            return formatCurrency(value);
          }
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };
  
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          color: theme.palette.text.secondary,
        },
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: alpha(theme.palette.divider, 0.2),
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatCurrency(context.parsed.y);
            }
            return label;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7,
        },
      },
      y: {
        beginAtZero: true,
        stacked: true,
        grid: {
          color: alpha(theme.palette.text.secondary, 0.1),
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          callback: function(value) {
            return formatCurrency(value);
          }
        },
      },
    },
  };
  
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          color: theme.palette.text.secondary,
        },
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: alpha(theme.palette.divider, 0.2),
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        callbacks: {
          label: function(context) {
            let label = context.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed !== null) {
              label += formatCurrency(context.parsed);
            }
            return label;
          }
        }
      },
    },
    layout: {
      padding: 20
    },
    cutout: '60%'
  };
  
  // Handle download of cost data as CSV
  const handleDownloadCSV = () => {
    // Create CSV content
    const headers = [
      'Date',
      'Compute',
      'Block Storage',
      'File Storage',
      'Object Storage',
      'Load Balancer',
      'VCN',
      'MySQL',
      'Functions',
      'Total'
    ].join(',');
    
    const rows = costData.map(item => [
      item.date,
      item.total_compute_cost || 0,
      item.total_block_storage_cost || 0,
      item.total_file_storage_cost || 0,
      item.total_object_storage_cost || 0,
      item.total_load_balancer_cost || 0,
      item.total_virtual_cloud_network_cost || 0,
      item.total_mysql_cost || 0,
      item.total_functions_cost || 0,
      item.grand_total_cost || 0
    ].join(','));
    
    const csvContent = [headers, ...rows].join('\n');
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `oci_costs_${startDateStr}_${endDateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="OCI Cost Analysis"
        subtitle="Analyze and track Oracle Cloud Infrastructure costs"
        icon={CostIcon}
        actions={
          <Box display="flex" gap={2} flexWrap="wrap">
            {/* Back button */}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              component={Link}
              to="/oci"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
              }}
            >
              Back to Dashboard
            </Button>
            
            {/* Refresh button */}
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={handleRefresh}
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            {/* Export button */}
            <Tooltip title="Export Cost Data">
              <IconButton 
                onClick={handleDownloadCSV}
                sx={{
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.success.main, 0.2),
                  },
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      
      {/* Filters */}
      <Paper 
        sx={{ 
          p: 2, 
          mb: 3,
          borderRadius: '16px',
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center'
        }}
      >
        {/* Region Selector */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Region</InputLabel>
          <Select
            value={region}
            label="Region"
            onChange={(e) => setRegion(e.target.value)}
            disabled={loading.regions}
            startAdornment={
              <FilterIcon fontSize="small" sx={{ ml: 1, mr: 0.5, color: theme.palette.text.secondary }} />
            }
          >
            <MenuItem value="">All Regions</MenuItem>
            {regions.map((reg) => (
              <MenuItem key={reg} value={reg}>{reg}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* Compartment Selector */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Compartment</InputLabel>
          <Select
            value={compartment}
            label="Compartment"
            onChange={(e) => setCompartment(e.target.value)}
            disabled={loading.compartments}
            startAdornment={
              <FilterIcon fontSize="small" sx={{ ml: 1, mr: 0.5, color: theme.palette.text.secondary }} />
            }
          >
            <MenuItem value="">All Compartments</MenuItem>
            {compartments.map((comp) => (
              <MenuItem key={comp} value={comp}>{comp}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* Date Range - using simple text fields instead of DatePicker */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            label="Start Date"
            type="date"
            value={startDateStr}
            onChange={(e) => setStartDateStr(e.target.value)}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ width: 160 }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDateStr}
            onChange={(e) => setEndDateStr(e.target.value)}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ width: 160 }}
          />
        </Box>
        
        {/* Cost Summary */}
        <Box ml="auto" display="flex" flexDirection="column" alignItems="flex-end">
          <Typography variant="caption" color="text.secondary">
            Total Cost ({costData.length} days)
          </Typography>
        </Box>
      </Paper>
      
      {/* Error message */}
      {error && (
        <Paper 
          sx={{ 
            p: 2, 
            mb: 3, 
            borderRadius: '16px',
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Typography>{error}</Typography>
        </Paper>
      )}
      
      {/* Remove LoadingOverlay and display content directly */}
        {costData.length === 0 ? (
          <EmptyState 
            title="No cost data available"
            message="No cost data is available for the selected filters. Try adjusting your filter criteria."
            icon={CostIcon}
          />
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={4}>
                <Card sx={{ 
                  borderRadius: '16px',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                  height: '100%'
                }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Cost Summary
                    </Typography>
                    <Divider sx={{ mb: 2, opacity: 0.1 }} />
                    
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Total Cost:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formatCurrency(costSummary.totalCost)}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Average Daily Cost:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formatCurrency(costSummary.averageDailyCost)}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Projected Monthly Cost:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formatCurrency(costSummary.projectedMonthlyCost)}
                        </Typography>
                      </Box>
                      
                      <Divider sx={{ my: 1, opacity: 0.1 }} />
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Compute:
                        </Typography>
                        <Typography variant="body1">
                          {formatCurrency(costSummary.costByService.compute)}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Storage:
                        </Typography>
                        <Typography variant="body1">
                          {formatCurrency(costSummary.costByService.storage)}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Network:
                        </Typography>
                        <Typography variant="body1">
                          {formatCurrency(costSummary.costByService.network)}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Database:
                        </Typography>
                        <Typography variant="body1">
                          {formatCurrency(costSummary.costByService.database)}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Other Services:
                        </Typography>
                        <Typography variant="body1">
                          {formatCurrency(costSummary.costByService.other)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={8}>
                <ChartCard
                  title="Daily Cost Trend"
                  subtitle="Total cost per day over the selected period"
                  height={310}
                  chart={
                    dailyCostTrendData ? (
                      <Line data={dailyCostTrendData} options={lineChartOptions} />
                    ) : (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography color="text.secondary">No cost data available</Typography>
                      </Box>
                    )
                  }
                />
              </Grid>
            </Grid>
            
            {/* Data vs Price Analysis Widget */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12}>
                <DataPriceAnalysisWidget 
                  initialRegion={region}
                  initialCompartment={compartment}
                  startDate={startDateStr}
                  endDate={endDateStr}
                />
              </Grid>
            </Grid>
            
            {/* Charts Row */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={6}>
                <ChartCard
                  title="Service Cost Distribution"
                  subtitle="Breakdown of costs by service category"
                  height={320}
                  chart={
                    serviceBreakdownData ? (
                      <Doughnut data={serviceBreakdownData} options={pieChartOptions} />
                    ) : (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography color="text.secondary">No service cost data available</Typography>
                      </Box>
                    )
                  }
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ChartCard
                  title="Service Cost Trend"
                  subtitle="Daily cost trend by service category"
                  height={320}
                  chart={
                    servicesTrendData ? (
                      <Bar data={servicesTrendData} options={barChartOptions} />
                    ) : (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography color="text.secondary">No service cost data available</Typography>
                      </Box>
                    )
                  }
                />
              </Grid>
            </Grid>
            
            {/* Detailed Charts Row */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={6}>
                <ChartCard
                  title="Storage Costs Breakdown"
                  subtitle="Costs by storage type"
                  height={280}
                  chart={
                    storageBreakdownData ? (
                      <Pie data={storageBreakdownData} options={pieChartOptions} />
                    ) : (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography color="text.secondary">No storage cost data available</Typography>
                      </Box>
                    )
                  }
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ChartCard
                  title="Other Services Costs"
                  subtitle="Costs for miscellaneous services"
                  height={280}
                  chart={
                    otherCostsBreakdownData ? (
                      <Pie data={otherCostsBreakdownData} options={pieChartOptions} />
                    ) : (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography color="text.secondary">No other service cost data available</Typography>
                      </Box>
                    )
                  }
                />
              </Grid>
            </Grid>
            
            {/* Costs Table */}
            <Paper sx={{ 
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`
            }}>
              <Typography variant="h6" sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                Daily Cost Details
              </Typography>
              
              <TableContainer>
              <Table>
                  <TableHead>
                    <TableRow sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      '& th': {
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                      }
                    }}>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Compute</TableCell>
                      <TableCell align="right">Storage</TableCell>
                      <TableCell align="right">Network</TableCell>
                      <TableCell align="right">Database</TableCell>
                      <TableCell align="right">Other</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCostData.map((item) => {
                      const storageCost = (
                        (item.total_block_storage_cost || 0) + 
                        (item.total_file_storage_cost || 0) + 
                        (item.total_object_storage_cost || 0)
                      );
                      const networkCost = (
                        (item.total_load_balancer_cost || 0) + 
                        (item.total_virtual_cloud_network_cost || 0)
                      );
                      const otherCost = (
                        (item.total_functions_cost || 0) + 
                        (item.total_telemetry_cost || 0) + 
                        (item.total_api_gateway_cost || 0) + 
                        (item.total_container_engine_cost || 0) + 
                        (item.total_key_management_cost || 0) + 
                        (item.total_logging_cost || 0)
                      );
                      
                      return (
                        <TableRow 
                          key={item.date}
                          hover
                          sx={{
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.03),
                            },
                            '& td': {
                              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                            }
                          }}
                        >
                          <TableCell>{item.date}</TableCell>
                          <TableCell align="right">{formatCurrency(item.total_compute_cost || 0)}</TableCell>
                          <TableCell align="right">{formatCurrency(storageCost)}</TableCell>
                          <TableCell align="right">{formatCurrency(networkCost)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.total_mysql_cost || 0)}</TableCell>
                          <TableCell align="right">{formatCurrency(otherCost)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                            {formatCurrency(item.grand_total_cost || 0)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 30]}
                component="div"
                count={costData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </>
        )}
    </Box>
  );
};

export default OCICosts;